import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { CustomMatPaginatorIntl } from './custom-mat-paginator-intl';

@UntilDestroy()
@Component({
  selector: 'siat-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss'],
  imports: [
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatPaginatorModule,
    MatSelectModule,
    TranslatePipe,
    MatTooltip,
  ],
  providers: [{ provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl }],
})
export class PaginationComponent
  implements OnInit, AfterViewInit, OnDestroy, OnChanges
{
  /**
   * The length of the list to be paginated.
   */
  @Input()
  length = 0;

  /**
   * The number of items to display per page.
   */
  @Input()
  pageSize = 10;

  /**
   * The current page index (0-based).
   */
  @Input()
  pageIndex = 0;

  /**
   * The total number of pages.
   */
  @Input()
  totalPages = 0;

  /**
   * The arrow forward enaled.
   */
  @Input()
  arrowForward = true;

  /**
   * The page size options to display in the dropdown.
   */
  @Input()
  pageSizeOptions: number[] = [10, 25, 50, 75, 100];

  /**
   * Emit an event when the page is changed.
   */
  @Output()
  page: EventEmitter<PageEvent> = new EventEmitter<PageEvent>();

  @ViewChild('pageInput') pageInput!: ElementRef<HTMLInputElement>;

  private wheelListener = (event: WheelEvent) => event.preventDefault();

  /**
   * The FormControl used to manage the page navigation.
   */
  pageFormControl = new FormControl(1);

  /**
   * @inheritdoc
   */
  ngOnInit() {
    if (this.totalPages) {
      this.syncFormControlValue();
      this.pageFormControl.valueChanges
        .pipe(debounceTime(400), distinctUntilChanged(), untilDestroyed(this))
        .subscribe((value) => {
          this.onInputGoToPage(value as number);
        });
    }
  }
  /**
   * @inheritdoc
   */
  ngAfterViewInit() {
    if (this.totalPages) {
      // Aggiungiamo il listener dopo che la vista è stata inizializzata
      this.pageInput.nativeElement.addEventListener(
        'wheel',
        this.wheelListener,
        { passive: false }, // Dichiariamo esplicitamente che è attivo
      );
    }
  }

  /**
   * @inheritdoc
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['pageIndex'] || changes['totalPages']) {
      this.syncFormControlValue();
    }
  }

  /**
   * Synchronize the FormControl's value with the pageIndex value.
   */
  private syncFormControlValue(): void {
    const newPageIndexValue = this.pageIndex + 1;

    if (this.totalPages === 0) {
      if (this.pageFormControl.value !== 0) {
        this.pageFormControl.setValue(0, { emitEvent: false });
      }
    } else {
      const clampedValue = Math.max(
        1,
        Math.min(newPageIndexValue, this.totalPages),
      );

      if (this.pageFormControl.value !== clampedValue) {
        this.pageFormControl.setValue(clampedValue, { emitEvent: false });
      }
    }
  }

  /**
   * Manage the page navigation and emit the PageEvent triggered by user input (blur/enter).
   * @param userInputPageIndex The 1-based page number entered by the user.
   */
  onInputGoToPage(userInputPageIndex: number | null): void {
    if (
      userInputPageIndex === null ||
      isNaN(userInputPageIndex) ||
      userInputPageIndex < 1 ||
      (this.totalPages > 0 && userInputPageIndex > this.totalPages)
    ) {
      this.syncFormControlValue();
      return;
    }

    const newPageIndex = userInputPageIndex - 1; // Converti a 0-based
    if (newPageIndex === this.pageIndex) return;

    this.emitPageEvent(newPageIndex, this.pageSize);
  }

  /**
   * Go to the first page.
   */
  goToFirstPage(): void {
    if (!this.isFirstPage) {
      this.emitPageEvent(0, this.pageSize);
    }
  }

  /**
   * Go to the previous page.
   */
  goToPreviousPage(): void {
    if (!this.isFirstPage) {
      this.emitPageEvent(this.pageIndex - 1, this.pageSize);
    }
  }

  /**
   * Go to the next page.
   */
  goToNextPage(): void {
    if (!this.isLastPage) {
      this.emitPageEvent(this.pageIndex + 1, this.pageSize);
    }
  }

  /**
   * Go to the last page.
   */
  goToLastPage(): void {
    if (!this.isLastPage) {
      this.emitPageEvent(this.totalPages - 1, this.pageSize);
    }
  }

  /**
   * On page size change.
   * (Note: This method is not used in the current HTML template for the specific layout)
   */
  onPageSizeChange(newSize: number): void {
    if (this.pageSize !== newSize) {
      this.emitPageEvent(0, newSize);
    }
  }

  /**
   * Private method to emit a PageEvent when the page is changed.
   * This component does NOT update its own pageIndex/pageSize internally;
   * it relies on the parent component to update these @Inputs after
   * fetching new data from the backend.
   */
  private emitPageEvent(newPageIndex: number, newPageSize: number): void {
    const previousPageIndex = this.pageIndex;

    this.page.emit({
      pageIndex: newPageIndex,
      pageSize: newPageSize,
      length: this.length, // 'length' remains what it currently is, parent updates it.
      previousPageIndex: previousPageIndex,
    });

    // IMPORTANT: Do NOT update this.pageIndex or this.pageSize here.
    // These will be updated by the parent component when it receives
    // the new data from the backend and passes the updated values
    // back to this component via its @Inputs.
    // The `syncFormControlValue()` call for the UI input will be handled by ngOnChanges.
  }

  /**
   * Disables the "previous" button if we're on the first page.
   */
  get isFirstPage(): boolean {
    return this.pageIndex === 0 || this.totalPages === 0;
  }

  /**
   * Disables the "next" button if we're on the last page.
   */
  get isLastPage(): boolean {
    return this.pageIndex === this.totalPages - 1 || this.totalPages === 0;
  }

  /**
   * @inheritdoc
   */
  ngOnDestroy(): void {
    if (this.pageInput) {
      this.pageInput.nativeElement.removeEventListener(
        'wheel',
        this.wheelListener,
      );
    }
  }
}
